import {
  PlusOutlined,
  ShoppingOutlined,
  SmileOutlined,
} from "@ant-design/icons";
import { message, Result, Spin } from "antd";
import Avatar from "boring-avatars";
import { DateTime } from "luxon";
import { useState } from "react";
import DocumentTitle from "react-document-title";
import { generatePath, Link, useNavigate, useParams } from "react-router";
import { imagePlaceholder } from "../../common";
import {
  ReservedModifier,
  useCurrentUser,
  useProfile,
  useWishlist,
} from "../../data/hooks";
import { apostrophizeName } from "../../data/strings";
import Button from "../Components/Button";
import DeleteWishButton from "../Components/DeleteWishButton";
import EditWishButton from "../Components/EditWishButton";
import MobileHeader from "../Components/MobileHeader";
import BackButton from "../Components/MobileHeader/BackButton";
import SubHeader from "../Components/SubHeader";
import { RoutePaths } from "../routes";
import WishImage from "./Wishlist/WishImage";
import { Reserved, Id, Wishlist } from "../../data/types";
import {
  analyticsLogEvent,
  analyticsReportError,
} from "../../data/firebase/analytics";

interface ReserveWishButtonProps {
  wishlistId?: Id;
  wishId?: Id;
  userId?: Id;
  wishlist?: Wishlist;
  isLoaded: boolean;
  reserved?: Reserved[];
  reserve?: ReservedModifier;
  unreserve?: ReservedModifier;
}

function ReserveWishButton({
  wishlistId,
  wishId,
  userId,
  wishlist,
  isLoaded: isWishlistLoaded,
  reserved,
  reserve,
  unreserve,
}: ReserveWishButtonProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  if (!wishlistId || !wishId) {
    analyticsLogEvent("wish_page_no_id");
    return <></>;
  }

  if (wishlist?.userId === userId) {
    return <></>;
  }

  const reservedRec = reserved?.find(
    (r) => r.wishlistId === wishlistId && r.wishId === wishId,
  );
  const isReservedByMe =
    reservedRec && reservedRec?.userId && reservedRec?.userId === userId;
  const isReservedBySomeoneElse =
    reservedRec && reservedRec?.userId && reservedRec?.userId !== userId;

  const toggleReserved = async () => {
    if (!userId) {
      navigate(generatePath(RoutePaths.login));
      return;
    }
    setIsLoading(true);
    try {
      if (isReservedByMe && unreserve) {
        analyticsLogEvent("unreserve", { wishlistId, wishId, userId });
        await unreserve({
          wishlistId,
          wishId,
          userId,
        });
      } else if (!isReservedByMe && reserve) {
        analyticsLogEvent("reserve", { wishlistId, wishId, userId });
        await reserve({
          wishlistId,
          wishId,
          userId,
        });
      }
    } catch (err) {
      analyticsReportError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isReservedBySomeoneElse ? (
        <div>
          <div className="inline-block h-10 rounded-full border px-4 py-2">
            ✨ This gift is reserved by someone else!
          </div>
        </div>
      ) : (
        <Button
          loading={isLoading || !isWishlistLoaded}
          onClick={toggleReserved}
          lighter={isReservedByMe || false}
        >
          ✨ {isReservedByMe ? "Unreserve" : "Reserve"} wish
        </Button>
      )}
    </>
  );
}

export default function Wish() {
  const { wishId, wishlistId } = useParams();
  const {
    isLoaded: isWishlistLoaded,
    wishlist,
    updateWish,
    deleteWish,
    reserved,
    reserve,
    unreserve,
  } = useWishlist();
  const { user } = useCurrentUser();
  const wishlistOwnerProfile = useProfile(wishlist?.userId);

  const wish = wishlist?.items.find((w) => w.id === wishId);

  const [messageApi, contextHolder] = message.useMessage();

  if (!wishlist) {
    return <Spin />;
  }

  if (!wish) {
    const path = wishlistId
      ? generatePath(RoutePaths.specificWishlist, { wishlistId })
      : generatePath(RoutePaths.root);

    analyticsLogEvent("wish_not_found", { wishlistId, wishId });

    return (
      <Result
        icon={<SmileOutlined />}
        title="Wish not found"
        subTitle="Maybe owner deleted it?"
        extra={
          <Link to={path} viewTransition>
            <Button>Back {wishlistId ? "to wishlist" : "home"}</Button>
          </Link>
        }
      />
    );
  }

  const isMyWish = wishlist.userId == user?.uid;
  const title = isMyWish
    ? "My wish"
    : apostrophizeName(wishlistOwnerProfile?.displayName) + " wish";

  const addToMyWishlist = () => {
    messageApi.info("Not implemented yet");
    analyticsLogEvent("add_to_my_wishlist", { wishlistId, wishId });
  };

  const wishlistLink = generatePath(RoutePaths.specificWishlist, {
    wishlistId: wishlist.id,
  });

  const deleteThisWish = deleteWish
    ? async () => {
        try {
          await deleteWish(wish);
          analyticsLogEvent("delete_wish", { wishlistId, wishId });
        } catch (err) {
          analyticsReportError(err as Error);
        }
      }
    : undefined;

  return (
    <DocumentTitle title={title}>
      <>
        <MobileHeader title={title}>
          {updateWish ? (
            <>
              <EditWishButton
                wish={wish}
                updateWish={updateWish}
                deleteWish={deleteThisWish}
              />
              <DeleteWishButton icon deleteWish={deleteThisWish} />
              <div className="grow"></div>
            </>
          ) : (
            <>
              <div></div>
            </>
          )}
          <BackButton link={wishlistLink} />
        </MobileHeader>
        <SubHeader>
          <div className="flex justify-start">
            <BackButton link={wishlistLink} />
            <div className="h-5"></div>
          </div>
          <div className="flex justify-end">
            {updateWish ? (
              <>
                <EditWishButton
                  wish={wish}
                  updateWish={updateWish}
                  deleteWish={deleteThisWish}
                />
                <DeleteWishButton icon deleteWish={deleteThisWish} />
              </>
            ) : (
              <></>
            )}
          </div>
        </SubHeader>
        <div className="container gap-8 p-8 md:columns-2">
          <div className="flex aspect-square min-w-20 items-center overflow-hidden rounded-3xl">
            <WishImage imageLink={wish?.imageLink || imagePlaceholder} full />
            <div> </div>
          </div>

          <div className="flex h-full flex-col gap-6">
            <div>
              <div className="H1 line-clamp-2" title={wish.name}>
                {wish.name}
              </div>
              <div className="flex flex-row items-center justify-between">
                <div className="H4">
                  {wish?.price} {wish?.price && wish?.currency}{" "}
                </div>
                <div className="BodySmall" title={wish?.createdAt}>
                  {DateTime.fromISO(wish?.createdAt).toRelative()}
                </div>
              </div>
            </div>
            {!isMyWish && (
              <div className="flex flex-col gap-4">
                <Avatar name={wishlist.userId} size={48} variant={"beam"} />
                <div className="H6">
                  {wishlistOwnerProfile?.displayName || <i>Anonymous</i>}
                </div>
                <ReserveWishButton
                  wishlistId={wishlistId}
                  wishId={wishId}
                  userId={user?.uid}
                  isLoaded={isWishlistLoaded}
                  reserved={reserved}
                  reserve={reserve}
                  unreserve={unreserve}
                  wishlist={wishlist}
                />
              </div>
            )}
            <div className="flex gap-4">
              <Link to={wish?.link || ""} target="_blank" viewTransition>
                <Button light>
                  <ShoppingOutlined />
                  Go to store
                </Button>
              </Link>
              {contextHolder}
              {!isMyWish && (
                <Button light onClick={addToMyWishlist}>
                  <PlusOutlined />
                  Add to my wishlist
                </Button>
              )}
            </div>
            <div className="Body">{wish?.description}</div>
          </div>
        </div>
      </>
    </DocumentTitle>
  );
}
